import { ChangeDetectorRef, ComponentRef, ViewContainerRef } from "@angular/core";
import { NoteEntryTocComponent } from "ng/src/app/notes/note-entry-toc/note-entry-toc.component";
import { NoteControlOptions } from "ng/src/app/notes/note-controller/note-controller-options.model";
import { Observable, Subject, Subscription } from "rxjs";
import { Note } from "ng/src/app/notes/shared/note.model";
import { TocMode } from "ng/src/app/notes/shared/note-entry.model";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { Utils } from "ng/src/app/core/utils";

export class ViewContainerDetails {
	private _container?: ViewContainerRef;
	private _hadContainerAssigned = false;
	
	barSettings: NoteControlOptions["BAR"] = {
		maximized: false
	};
	dialogSettings: NoteControlOptions["DIALOG"] = ViewContainerDetails.userDefaultDialogSettings();
	enterNoteSettings: NoteControlOptions["ENTER_NOTES"] = {};
	
	onContainerAssigned?: (details: ViewContainerDetails) => void;
	
	constructor(
		readonly component: ComponentRef<NoteEntryTocComponent>,
		container?: ViewContainerRef
	) {
		this.container = container;
	}
	
	get container() {
		return this._container;
	}
	set container(val) {
		let idx;
		if (this._container && ((idx = this._container.indexOf(this.component.hostView)) > -1)) {
			this._container.detach(idx)
		}
		
		this._container = val;
		
		if (val) {
			val.insert(this.component.hostView);
			this.component.changeDetectorRef.detectChanges();
			
			if (!this._hadContainerAssigned) {
				this._hadContainerAssigned = true;
				this.onContainerAssigned?.(this);
			}
		}
	}
	
	removeFromContainer() {
		this.container = undefined;
	}
	
	hasWork() {
		const service = this.component.instance.noteEntryService;
		return service.hasPendingChanges || service.hasPendingSectionChanges || !!service.editingNote;
	}
	
	static userDefaultDialogSettings(): ViewContainerDetails['dialogSettings'];
	static userDefaultDialogSettings(newVal: Omit<ViewContainerDetails['dialogSettings'], 'maximized'>): undefined;
	static userDefaultDialogSettings(newVal?: Omit<ViewContainerDetails['dialogSettings'], 'maximized'>): ViewContainerDetails['dialogSettings'] | undefined {
		const prefs = AppStateService.preferences;
		const key = 'default-popup-prefs';
		if (!newVal) {
			return Utils.deepCopy(prefs.getValue(key, this.defaultDialogSettings()))
		}
		const savingValue = Utils.deepCopy(newVal);
		delete savingValue.maximized;
		prefs.setValue(key, savingValue);
		return;
	}
	
	static defaultDialogSettings(): ViewContainerDetails['dialogSettings'] {
		return {
			style: { width: '500px', minWidth: '500px', minHeight: '500px', height: '500px'},
			coordinates: {},
			maximized: false
		}
	}
}

export interface NoteControlResult {
	destroy: Observable<any>,
	notePublished: Observable<Note | null>,
	accessRevoked: Observable<any>,
	tocMode: Observable<TocMode>
}

export interface DialogDetails {
	subscription: Subscription,
	details: ViewContainerDetails,
	changeDetector: ChangeDetectorRef,
	element?: HTMLDivElement
}


export type NoteControlContainerTypes = {
	popup: ViewContainerDetails[];
	bar: ViewContainerDetails[];
	hidden: ViewContainerDetails[];
	other: ViewContainerDetails[];
}

export type NoteControlContainerTypeKeys = keyof NoteControlContainerTypes;

export class NoteControlSource implements NoteControlContainerTypes {
	readonly popup: ViewContainerDetails[] = [];
	readonly bar: ViewContainerDetails[] = [];
	readonly hidden: ViewContainerDetails[] = [];
	readonly other: ViewContainerDetails[] = [];
	
	getSrcMap(): NoteControlContainerTypes {
		return {
			popup: this.popup,
			bar: this.bar,
			hidden: this.hidden,
			other: this.other
		}
	}
	
	find(details: ViewContainerDetails): { index: number, type: NoteControlContainerTypeKeys, src: ViewContainerDetails[] } | null {
		const srcMap: NoteControlContainerTypes = this.getSrcMap();
		
		for (const key in this.getSrcMap()) {
			const srcArr = srcMap[key as NoteControlContainerTypeKeys];
			const foundIdx = srcArr.indexOf(details);
			if (foundIdx === -1) continue;
			return {
				index: foundIdx,
				type: key as NoteControlContainerTypeKeys,
				src: srcArr
			}
		}
		
		return null;
	}
	
	move(details: ViewContainerDetails, to: NoteControlContainerTypeKeys, position: 'start' | 'end' = 'start') {
		return this.runUpdating(() => {
			const current = this.find(details);
			details.removeFromContainer();
			
			if (current) {
				if (to === current.type) {
					const insertionIdx = getInsertionIndex(position, current.src);
					if (insertionIdx === current.index)
						return;
					
					this.remove(current.index, current.type);
					current.src.splice(insertionIdx, 0, details);
					return;
				}
				this.remove(current.index, current.type);
			}
			
			
			this[to].splice(getInsertionIndex(position, this[to]), 0, details);
		});
		
		function getInsertionIndex(position: 'start' | 'end', src: any[]) {
			return position === 'start' ? 0 : src.length - 1;
		}
	}
	
	remove(index: number, type: NoteControlContainerTypeKeys): ViewContainerDetails[];
	remove(toRemove: ViewContainerDetails | ((details: ViewContainerDetails) => boolean), type?: NoteControlContainerTypeKeys): ViewContainerDetails[]
	remove(toRemove: ViewContainerDetails | number | ((details: ViewContainerDetails) => boolean), type?: NoteControlContainerTypeKeys): ViewContainerDetails[] {
		return this.runUpdating(() => {
			if (type) {
				const src = this[type];
				const idx = typeof toRemove === 'number' ? toRemove : src.findIndex(matches);
				if (idx === -1) return [];
				return src.splice(idx, 1);
			}
			
			const srcMap = this.getSrcMap();
			let removed: ViewContainerDetails[] = [];
			for (const key in srcMap) {
				const src = srcMap[key as NoteControlContainerTypeKeys];
				const idx = typeof toRemove === 'number' ? toRemove : src.findIndex(matches);
				if (idx === -1) continue;
				removed = removed.concat(src.splice(idx, 1));
			}
			
			return removed;
		});
		
		function matches(src: ViewContainerDetails) {
			return typeof toRemove === 'function' ? toRemove(src) : (src === toRemove)
		}
	}
	
	private inUpdate = false;
	protected runUpdating<R>(func: () => R): R {
		let result: R;
		
		const innerUpdate = this.inUpdate;
		this.inUpdate = true;
		
		try {
			result = func();
		}
		finally {
			if (!innerUpdate) {
				this.updateSubject.next();
				this.inUpdate = false;
			}
		}
		
		return result;
	}
	
	private readonly updateSubject = new Subject();
	readonly update$ = this.updateSubject.asObservable();
}